import { useState, useMemo } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Caption1, Spinner, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useActivityHeatmap } from '../../../api/hooks';

// FEAT-023 Activity Heat-map. GitHub-style calendar grid: rows = day-of-week,
// columns = ISO weeks back from today. Intensity scaled within the visible window so
// quiet projects still show variation.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  card: {
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateRows: 'repeat(7, 1rem)',
    gridAutoFlow: 'column',
    gap: '0.125rem',
  },
  cell: {
    width: '1rem',
    height: '1rem',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
  legendSwatch: {
    width: '1rem', height: '1rem', borderRadius: tokens.borderRadiusSmall,
  },
});

// Five-bucket scale using the Fluent brand palette so every intensity respects tokens.
const INTENSITY_TOKENS = [
  tokens.colorNeutralBackground3,
  tokens.colorBrandBackground2Hover,
  tokens.colorBrandBackground2Pressed,
  tokens.colorBrandBackground,
  tokens.colorBrandBackgroundHover,
];

export function ActivityHeatmapPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useActivityHeatmap(projectId, 90);

  const cellsByDate = useMemo(() => {
    const map = new Map<string, number>();
    data?.cells.forEach((c) => map.set(c.day, c.count));
    return map;
  }, [data]);

  const maxCount = useMemo(() =>
    data?.cells.reduce((max, c) => Math.max(max, c.count), 0) ?? 0
  , [data]);

  // Build a 90-day grid ending today.
  const grid = useMemo(() => {
    const days: { iso: string; count: number }[] = [];
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      days.push({ iso, count: cellsByDate.get(iso) ?? 0 });
    }
    return days;
  }, [cellsByDate]);

  function intensity(count: number): string {
    if (maxCount === 0 || count === 0) return INTENSITY_TOKENS[0];
    const ratio = count / maxCount;
    if (ratio > 0.75) return INTENSITY_TOKENS[4];
    if (ratio > 0.5) return INTENSITY_TOKENS[3];
    if (ratio > 0.25) return INTENSITY_TOKENS[2];
    return INTENSITY_TOKENS[1];
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Activity Heatmap</Title2>
        <Body1 className={styles.hint}>
          Work-item revisions per day, last 90 days. Quiet stretches show up immediately — useful for spotting projects that have gone dormant.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading activity…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : (
        <div className={styles.card}>
          <div className={styles.grid} aria-label="Activity heatmap, last 90 days">
            {grid.map(({ iso, count }) => (
              <div
                key={iso}
                className={styles.cell}
                style={{ backgroundColor: intensity(count) }}
                title={`${iso} — ${count} change${count === 1 ? '' : 's'}`}
              />
            ))}
          </div>
          <div className={styles.legend}>
            <Caption1>Less</Caption1>
            {INTENSITY_TOKENS.map((t, i) => (
              <span key={i} className={styles.legendSwatch} style={{ backgroundColor: t }} />
            ))}
            <Caption1>More</Caption1>
          </div>
        </div>
      )}
    </div>
  );
}
